import React, { useState, useEffect } from 'react';
import pointsService from '../../services/pointsService';
import userService from '../../services/userService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateForm, setUpdateForm] = useState({
    userId: '',
    amount: '',
    reason: '',
    loading: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Ungültiges Datum';
    return date.toLocaleDateString('de-DE');
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('Loading users...'); // Debug log
      // Use userService for admin functionality
      const response = await userService.getAllUsers();
      console.log('Users response:', response.data); // Debug log
      if (response.data.success) {
        setUsers(response.data.users);
        console.log('Users loaded:', response.data.users.length); // Debug log
      }
    } catch (error) {
      console.error('Error loading users:', error); // Debug log
      setError('Fehler beim Laden der Benutzer');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsUpdate = async (e) => {
    e.preventDefault();
    setUpdateForm(prev => ({ ...prev, loading: true }));
    setError('');
    setSuccess('');

    try {
      const response = await pointsService.updatePoints(
        updateForm.userId,
        parseInt(updateForm.amount),
        updateForm.reason
      );

      if (response.data.success) {
        setSuccess('Punkte erfolgreich aktualisiert!');
        setUpdateForm({
          userId: '',
          amount: '',
          reason: '',
          loading: false
        });
        loadUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Fehler beim Aktualisieren der Punkte');
    } finally {
      setUpdateForm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    if (window.confirm(
      currentStatus 
        ? 'Admin-Status entfernen?' 
        : 'Benutzer zum Administrator machen?'
    )) {
      try {
        const response = await userService.toggleAdminStatus(userId);
        if (response.data.success) {
          setSuccess(response.data.message);
          setError('');
          loadUsers();
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Fehler beim Ändern des Admin-Status');
        setSuccess('');
      }
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Benutzer "${username}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden!`)) {
      try {
        const response = await userService.deleteUser(userId);
        if (response.data.success) {
          setSuccess(response.data.message);
          setError('');
          loadUsers();
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Fehler beim Löschen des Benutzers');
        setSuccess('');
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h5>Benutzer Verwaltung</h5>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      {/* Points Update Form */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">Punkte vergeben/abziehen</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handlePointsUpdate} className="row g-3">
            <div className="col-md-3">
              <select
                className="form-select"
                value={updateForm.userId}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, userId: e.target.value }))}
                required
              >
                <option value="">Benutzer wählen...</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.username} ({user.points} Punkte)
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                placeholder="Punkte"
                value={updateForm.amount}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, amount: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Grund für Änderung"
                value={updateForm.reason}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, reason: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-3">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={updateForm.loading}
              >
                {updateForm.loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Aktualisieren...
                  </>
                ) : (
                  'Punkte aktualisieren'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Username</th>
              <th>Punkte</th>
              <th>Admin</th>
              <th>Registriert</th>
              <th>Letzte Aktivität</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id}>
                <td>
                  <strong>{user.username}</strong>
                </td>
                <td>
                  <span className="badge bg-primary">
                    {user.points}
                  </span>
                </td>
                <td>
                  {user.isAdmin ? (
                    <span className="badge bg-warning">Admin</span>
                  ) : (
                    <span className="badge bg-secondary">User</span>
                  )}
                </td>
                <td>
                  <small className="text-muted">
                    {formatDate(user.createdAt)}
                  </small>
                </td>
                <td>
                  <small className="text-muted">
                    {user.pointHistory.length > 0 
                      ? formatDate(user.pointHistory[user.pointHistory.length - 1].date)
                      : 'Keine'
                    }
                  </small>
                </td>
                <td>
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      className={`btn ${user.isAdmin ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleAdmin(user._id, user.isAdmin)}
                      title={user.isAdmin ? 'Admin-Status entfernen' : 'Zum Admin machen'}
                    >
                      {user.isAdmin ? '👤 Admin entfernen' : '⭐ Admin machen'}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDeleteUser(user._id, user.username)}
                      title="Benutzer löschen"
                    >
                      🗑️ Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="alert alert-info">
          Keine Benutzer gefunden.
        </div>
      )}
    </div>
  );
};

export default UserManagement;
